/**
 * SessionState - Current phase of the multiplayer session
 */
export enum SessionState {
    LOBBY = 'lobby',
    IN_ROUND = 'in_round',
    ROUND_END = 'round_end',
}

/**
 * SessionManager - Manages room persistence and round transitions
 */
export class SessionManager {
    private state: SessionState = SessionState.IN_ROUND; // Rounds usually start immediately upon scene load
    private readyPlayers: Set<string> = new Set();
    private connectedPlayers: Set<string> = new Set();

    get currentState(): SessionState { return this.state; }

    init(myPlayerId: string, remotePlayerIds: string[]): void {
        this.connectedPlayers.clear();
        this.connectedPlayers.add(myPlayerId);
        remotePlayerIds.forEach(id => this.connectedPlayers.add(id));
        this.readyPlayers.clear();
        this.state = SessionState.IN_ROUND;
    }

    addPlayer(id: string): void {
        this.connectedPlayers.add(id);
    }

    removePlayer(id: string): void {
        this.connectedPlayers.delete(id);
        this.readyPlayers.delete(id);
    }

    startRound(): void {
        this.state = SessionState.IN_ROUND;
        this.readyPlayers.clear();
    }

    endRound(): void {
        this.state = SessionState.ROUND_END;
    }

    setPlayerReady(id: string): void {
        this.readyPlayers.add(id);
    }

    isEveryoneReady(): boolean {
        // Must have at least one player connected
        if (this.connectedPlayers.size === 0) return false;

        // Everyone who is CURRENTLY connected must be ready
        return this.readyPlayers.size >= this.connectedPlayers.size;
    }

    getReadyCount(): number {
        return this.readyPlayers.size;
    }

    getTotalPlayers(): number {
        return this.connectedPlayers.size;
    }

    getConnectedPlayerIds(): string[] {
        return Array.from(this.connectedPlayers);
    }
}
